import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import PageTitle from '../components/PageTitle'
import { pageTitleTexts } from '../../constants/texts/pageTitleTexts'
import { Formik, Form } from 'formik'
import FormObserver from '../components/FormElements/FormObserver'
import { FormInput, FormRadioButton, FormSelector } from '../components/FormElements'
import InstallmentCheckBoxes from '../components/InstallmentCheckBoxes'
import { useLoading } from '../../hooks/useLoading'
import commonNetwork from '../../common/api/commonNetwork'
import { toast } from 'react-toastify'
import { getLinkInstallments } from '../../common/helpers/installmentHelpers'
import { Button } from '../components/Elements';
import PackageErrorModal from '../components/PackageErrorModal'
import PaymentLinkResultModal from '../components/PaymentLinkResultModal'
import { sendTypeIds } from '../../constants/options/sendOptions'
import { formatDate } from '../../lib/helpers'
import appHelpers from '../../common/helpers/appHelpers'
import { useDispatch, useSelector } from 'react-redux'
import { unlimitedPaymentLinkSchema } from '../../common/validators/unlimitedPaymentLinkSchema'
import { FaCopy } from 'react-icons/fa'
import { refreshLinksTable } from '../../common/helpers/refreshTables/refreshLinksTable'
import { ThunkDispatch } from 'redux-thunk'
import { usePermissionCheck } from '../../hooks/usePermissionCheck'
import { UserOperationTypes } from '../../common/types/AuthorizationProps'
import { getParameters } from '../../store/slices/parametersSlice'

type UnlimitedPaymentType = {
    amount: string,
    linkdesc: string,
    paymentType: string,
    vpId: number,
    installments: number[],
    expiredate: any,
    sendtypeid: any
}

const UnlimitedPaymentLink = () => {

    usePermissionCheck(UserOperationTypes["Serbest Ödeme Al"], "Serbest Ödeme Linki İşlemleri izni yok", "suresiz-odeme-linki");
    const { parameters } = useSelector((state: any) => state.parameters);

    const initialValues: UnlimitedPaymentType = {
        amount: "",
        linkdesc: "",
        paymentType: "dynamic",
        vpId: 0,
        installments: [1],
        expiredate: parameters.linkExpiryDate,
        sendtypeid: parameters.sendTypeId,
    };

    const formRef = useRef<any>();
    const isFormClearable = useRef(false);
    const linResultModalRef = useRef<any>();

    const isDemoUser = appHelpers.isDemoUser();

    const [formValues, setFormValues] = useState<UnlimitedPaymentType>(initialValues);
    const [isFetching, setIsFetching] = useState<boolean>(false);
    const [vpList, setVpList] = useState<any[]>([]);
    const [bankInstallments, setBankInstallments] = useState([]);
    const [vpDetailsList, setVpDetailsList] = useState([]);
    const [postLinkLoading, setPostLinkLoading] = useState<boolean>(false);
    const [statusModalVisible, setStatusModalVisible] = useState<boolean>(false);
    const [packageErrorMessage, setPackageErrorMessage] = useState<string>("");
    const [isPackageErrorModalVisible, setIsPackageErrorModalVisible] = useState<boolean>(false);
    const [paymentLink, setPaymentLink] = useState<string | any>(null);

    const loading = useLoading(isFetching);

    const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

    useLayoutEffect(() => {

        fetchData();
        dispatch(getParameters());
    }, []);

    useEffect(() => {
        updateInstallmentListByVP();
    }, [formValues.vpId, vpDetailsList]);

    useEffect(() => {
        if (statusModalVisible != true) {
            clearIfFormClearable();
        }
    }, [statusModalVisible]);

    const onUpdate = (value: any) => {
        if (!loading) {
            if (value.paymentType === "dynamic") {
                value.vpId = 0;
            } else {
                if (value.vpId === 0 && vpDetailsList.length > 0)
                    value.vpId = vpList[0].value;
            }
            setFormValues({ ...formValues, ...value });
        }
    };

    const onSubmit = async (actions: any) => {
        let installments = "";
        formValues.installments.map((installment: any) => {
            if (installments !== "") installments += ",";
            installments += installment;
        });

        let formObject = {
            ISUNEXPIREDLINK: true,
            INSTALLMENTS: installments,
            AMOUNT: formValues.amount.replaceAll(",", "").replace(".", ""),
            SENDTYPEID: 4,
            LINKDESC: formValues.linkdesc,
            VPID: formValues.vpId,
            EXPIREDATE: formatDate(new Date()),
            EXPIREDATETYPE: formValues.expiredate,
        };
        if (formObject.SENDTYPEID !== sendTypeIds.email && isDemoUser) {
            toast.error("Demo kullanımda sadece E-Mail ile gönderim yapabilirsiniz");
        }
        else {
            postPaymentLinksAfterControl(formObject, actions);
        }
    }

    const postPaymentLinksAfterControl = async (formObject: any, actions: any) => {

        const response = await commonNetwork.post("/payment-link", formObject);
        setPostLinkLoading(false);

        var successList = [], errorList = [];

        const element = response.data;
        if (response.isSuccess == false) {
            toast.error(response.errorMessage);
        } else {
            if ((!element.smsstatus || !element.mailstatus)) {
                errorList.push({
                    ...element,
                });
            } else {
                refreshLinksTable(dispatch);
                setPaymentLink(response.data.link);
                successList.push({
                    ...element
                });
            }

            setStatusModalVisible(true);

            if (errorList.length == 0) {
                isFormClearable.current = true;

            }
            linResultModalRef.current.showMessage(errorList, successList, true);

        }
    }

    const updateFormInstallments = (month: any) => {
        let temp = formValues.installments;
        if (temp.includes(month)) {
            temp = temp.filter((i: any) => i !== month);
        } else {
            temp.push(month);
        }
        temp = Array.isArray(temp) ? temp : [temp];
        setFormValues({ ...formValues, installments: temp });
    };

    const fetchData = async () => {
        setIsFetching(true);

        const res = await commonNetwork.get("/virtualpos/firmvirtualpos-v1");
        const response = res;
        let vpTemp: any[] = [];

        setIsFetching(false);
        if (res.isSuccess) {

            response.data.map((vp: any) => {
                if (vp.status) {
                    vpTemp.push({
                        value: vp.vpid,
                        label: vp.vpdisplayname,
                    });
                }
            });
            setVpList(vpTemp);

            setVpDetailsList(response.data);
            updateInstallmentListByVP();

        } else {
            toast.error(res.errorMessage);
        }
    };

    const updateInstallmentListByVP = () => {
        if (formValues.vpId !== 0) {
            let vpDetail: any = vpDetailsList.find(
                (item: any) => item.vpid === formValues.vpId
            );
            let installmentList = getLinkInstallments(vpDetail.installmentListV1);


            setBankInstallments(installmentList);
        }
    };

    function clearIfFormClearable() {
        if (isFormClearable.current) {
            isFormClearable.current = false;
            setTimeout(() => {
                clearFilters();
            }, 100);
        }
    }

    const clearFilters = () => {
        formRef.current.resetForm();
        setFormValues(initialValues);
    };

    const copyPaymentLink = () => {
        navigator.clipboard.writeText(paymentLink)
        toast.success("Link başarılı bir şekilde kopyalandı");
    }

    return (
        <div className="w-full">
            <PageTitle title={pageTitleTexts.unlimitedPaymentLink} />
            <div className="px-4 py-2 flex flex-col justify-start space-y-2">
                <div className="flex flex-row items-center">
                    <img src={"/icons/serbest-odeme-baslat-dark.svg"} className="h-6 w-6 black" />
                    <span className="text-lg font-bold leading-7 text-contrast-90 sm:truncate ml-2">
                        Süresiz Ödeme Linki Oluştur
                    </span>
                </div>
            </div>
            <Formik
                initialValues={formValues}
                validationSchema={unlimitedPaymentLinkSchema}
                enableReinitialize
                onSubmit={onSubmit}
                innerRef={formRef}
            >
                {({ }) => {
                    return (<Form>
                        <FormObserver watch={(val: any) => onUpdate(val)} />
                        <div className="mx-4 px-4 py-2 bg-white shadow-md mb-4 pb-4 mt-1 rounded-md">
                            <div className="px-4 grid grid-cols-1 gap-2 sm:gap-3 lg:grid-cols-3 xl:grid-cols-3 mb-4">
                                <div className="mt-3">
                                    <h1 className="font-bold text-contrast-90">Gönderim Bilgileri</h1>
                                    <span className="text-contrast-50 text-md">
                                        Gönderim ayarlarınızı ilgili alanları doldurarak belirleyiniz. Ödeme açıklaması zorunlu değildir.
                                    </span>
                                </div>
                                <div className='mt-3'>
                                    <FormInput
                                        className={"max-h-[70px] min-h-[32px]"}
                                        label="Açıklama"
                                        name="linkdesc"
                                        type="textarea"
                                        maxLength={101}
                                    />
                                </div>
                                <div className="mt-3">
                                    <FormInput
                                        label="Tutar*"
                                        name="amount"
                                        currencyformat="true"
                                        currencyicon="true"
                                        placeholder="0.00"
                                        maxLength={18}
                                        isTRCurrencyDisplay={true}
                                    />
                                </div>
                            </div>

                            <hr />

                            <div className=" px-4 grid grid-cols-1 gap-2 sm:gap-3 lg:grid-cols-3 xl:grid-cols-3 ">
                                <div className="mt-3">
                                    <h1 className="font-bold text-contrast-90">
                                        Ödeme Altyapısı
                                    </h1>
                                    <span className="text-contrast-50 text-md">
                                        Yapılacak ödemeye ait banka ve taksitleri seçiniz
                                    </span>
                                </div>
                                <div className="col-span-2 ">
                                    <div className="grid grid-cols-1 gap-2 sm:gap-4 lg:grid-cols-2 xl:grid-cols-2   ">
                                        <div className=" mt-8 ">
                                            <div className=" items-center flex flex-row">
                                                <FormRadioButton
                                                    items={[
                                                        { label: "Banka Seç", value: "manuel" },
                                                        { label: "Dinamik Yönlendirme", value: "dynamic" },
                                                    ]}
                                                    checked={formValues.paymentType}
                                                    name="paymentType"
                                                />
                                            </div>
                                        </div>
                                        <div className="mt-3 ">
                                            {formValues.paymentType === "manuel" ? (
                                                isFetching ? (
                                                    <div className="mt-4">Yükleniyor...</div>
                                                ) : vpList.length > 0 ? (
                                                    <FormSelector
                                                        options={vpList}
                                                        name="vpId"
                                                        defaultValue={vpList[0]}
                                                        label="Ödeme Altyapisi"
                                                        placement="auto"
                                                    // value={formValues.vpId}
                                                    />
                                                ) : (
                                                    <span className="text-base text-error">
                                                        Aktif sanal POS listeniz veritabanindan okunamamistir.
                                                        Yetkili ile iletisime geciniz.
                                                    </span>
                                                )
                                            ) : isFetching ? (
                                                <div className="mt-4">Yükleniyor...</div>
                                            ) : vpList.length > 0 ? (
                                                <div className=" mt-4 ">
                                                    <span className="text-base text-contrast-70">
                                                        *Kredi kartına göre ilgili ödeme kuruluşuna otomatik yönlendirilir.
                                                    </span>
                                                </div>
                                            ) : (
                                                <span className="text-base text-error">
                                                    Aktif sanal POS listeniz veritabanindan okunamamistir.
                                                    Yetkili ile iletisime geciniz.
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {formValues.paymentType === "manuel" && vpList.length > 0 && (
                                        <div className="col-span-2">
                                            <InstallmentCheckBoxes
                                                className="mt-3 grid grid-cols-2 gap-2 sm:gap-2 lg:grid-cols-3 xl:grid-cols-3"
                                                installments={bankInstallments}
                                                checkedlist={formValues.installments}
                                                callback={(val: any) => {
                                                    updateFormInstallments(val);
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="mt-3 justify-end mr-3 pb-3 flex flex-row">
                                <Button
                                    type="reset"
                                    color="gray"
                                    hover="gray"
                                    size="xs"
                                    className="mr-3"
                                    callback={() => {
                                        clearFilters()
                                    }}
                                >
                                    Tüm Alanları Temizle
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    hover="primary"
                                    size="xs"
                                    disabled={vpList.length < 1 ? true : (postLinkLoading)}
                                    loading={postLinkLoading}
                                >
                                    Oluştur
                                </Button>
                            </div>
                            {paymentLink != null ?
                                <>
                                    <hr />
                                    <div className="px-4 grid grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-3 mb-4">
                                        <div className="mt-3 lg:col-span-1">
                                            <h1 className="font-bold text-contrast-90">Ödeme Linki</h1>
                                            <span className="text-contrast-50 text-md">
                                                Oluşturmuş olduğunuz linke tıklayarak ödeme ekranına gidebilirsiniz. Ya da butona tıklayarak linki kopyalayabilirsiniz.
                                            </span>
                                        </div>

                                        <div className="mt-8 lg:col-span-2">
                                            <div className='flex'>
                                                <FaCopy className="mr-2 mt-1 cursor-pointer text-[#729aa5]" onClick={copyPaymentLink} />
                                                <a className='text-sky-700' href={paymentLink} target='_blank'>
                                                    {paymentLink}
                                                </a>
                                            </div>
                                        </div>
                                    </div>

                                </> :
                                ""
                            }
                        </div>
                        <PackageErrorModal errorMessage={packageErrorMessage} isVisible={isPackageErrorModalVisible} setIsVisible={setIsPackageErrorModalVisible}></PackageErrorModal>

                        <PaymentLinkResultModal ref={linResultModalRef} statusModalVisible={statusModalVisible} setStatusModalVisible={setStatusModalVisible} />
                    </Form>)
                }}
            </Formik>
        </div>
    )
}

export default UnlimitedPaymentLink